import './create-caregiver-account.screen.scss';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Link,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import {useContext, useEffect, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {useHistory, useLocation} from 'react-router-dom';
import {PublicContentPage} from '../../../components/public-content-page/public-content-page';
import {SplashScreen} from '../../../components/splash-screen/splash-screen';
import {AppContext} from '../../../contexts/app/context';
import {AppContextType} from '../../../contexts/app/types';
import {SnackBarContext} from '../../../contexts/snack-bar/context';
import {SnackBarContextType} from '../../../contexts/snack-bar/types';
import {ScreenPaths} from '../../../enums/screen-path';
import {translations} from '../../../i18n/translations';
import {InvitesRepository} from '../../../repositories/invites';
import {getBrandName} from '../../../utils/functions/get-product-name';
import {LoadingButton} from '@mui/lab';
import {RelationshipType} from '../../../backend/enums/relationship-type';
import {relationshipTypes} from '../../../utils/data/relationship-types';
import {PASSWORD_REGEX} from '../../../utils/constants/regex';
import {
  FULL_NAME_MAX_CHARACTERS,
  DISPLAY_NAME_MAX_CHARACTERS,
} from '../../../utils/constants/maximum-characters';
import {EditPicture} from '../../../components/edit-picture/edit-picture';
import {UserGender} from '../../../backend/enums/user-gender';
import {genders} from '../../../utils/data/genders';
import {Environment} from '../../../enums/environment';
import {PostCaregiverAccount} from '../../../backend/requests/post-caregiver-account';
import {UserType} from '../../../backend/enums/user-type';
import {AuthContextType} from '../../../contexts/auth/types';
import {AuthContext} from '../../../contexts/auth/context';
import {UsersRepository} from '../../../repositories/users';
import {getAuth, signInWithEmailAndPassword, UserCredential} from 'firebase/auth';
import {BackendApiManager} from '../../../managers/backend-api';
import {SelectInput} from '../../../components/select-input/select-input';
import {TextInput} from '../../../components/text-input/text-input';
import {PhoneNumberInput} from '../../../components/phone-number-input/phone-number-input';
import {BackendLanguageTag} from '../../../backend/enums/backend-language-tag';
import {languages} from '../../../utils/data/languages';
import {appSizes} from '../../../utils/constants/sizes';
import {PostStorageRequest} from '../../../backend/requests/post-storage-request';
import {StorageFileType} from '../../../backend/enums/storage-file-type';
import {StorageType} from '../../../backend/enums/storage-type';
import {
  StorageRequestResult,
  UserPictureStorageRequestData,
} from '../../../backend/interfaces/storage-request';
import {StorageRequestRepository} from '../../../repositories/storage-request';
import {PutUser} from '../../../backend/requests/put-user';
import {RelationshipInviteData} from '../../../backend/interfaces/relationship-invite-data';
import {InviteType} from '../../../backend/enums/invite-type';
import {AffiliationInviteData} from '../../../backend/interfaces/affiliation-invite-data';
import {
  OccupationInput,
  OccupationOption,
} from '../../../components/occupation-input/occupation-input';
import {PasswordInput} from '../../../components/password-input/password-input';
import i18next from 'i18next';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LoginIcon from '@mui/icons-material/Login';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import {
  emailValidation,
  nameValidation,
  phoneNumberValidation,
  taxPayerNumberValidation,
} from '../../../components/user-form/user-form-validations';
import {FlatSelectDate} from '../../../components/flat-select-date/flat-select-date';
import {getTranslatedLegalDocuments} from '../../../utils/functions/get-translated-legal-documents';

// Legal documents target
const LEGAL_DOCUMENTS_TARGET: string = 'create-caregiver-account-legal-documents';

export default function CreateCaregiverAccountScreen(props: {
  type: 'relationship' | 'affiliation';
}) {
  // Get context
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const appContext: AppContextType = useContext(AppContext);
  const authContext: AuthContextType = useContext(AuthContext);

  // Hold state
  /* Steps (only 0 through 2 are in the stepper):
        - Step 0 - Essential info
        - Step 1 - Personal info
        - Step 2 - Optional info
        - Step 3 - Create account success
        - Step 4 - Create account error 
    */
  const [activeStep, setActiveStep] = useState<number>(0);
  const [loadingInviteData, setLoadingInviteData] = useState<boolean>(true);
  const [inviteData, setInviteData] = useState<
    {voucherCode: string; targetUserName?: string} | undefined
  >();

  // ----- Step 0 - Essential info -----
  const [buttonStep0Disabled, setButtonStep0Disabled] = useState<boolean>(true);
  const [relationshipType, setRelationshipType] = useState<RelationshipType>(
    RelationshipType.Family
  );
  const [occupation, setOccupation] = useState<OccupationOption | undefined>();
  const [showOccupationError, setShowOccupationError] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string | undefined>();
  const [showEmailError, setShowEmailError] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string | undefined>();
  const [showPasswordError, setShowPasswordError] = useState<boolean>(false);
  const [repeatPassword, setRepeatPassword] = useState<string>('');
  const [repeatPasswordError, setRepeatPasswordError] = useState<string | undefined>();
  const [showRepeatPasswordError, setShowRepeatPasswordError] = useState<boolean>(false);
  const [hasAcceptedLegalDocuments, setHasAcceptedLegalDocuments] = useState<boolean>(false);
  const [hasAcceptedLegalDocumentsError, setHasAcceptedLegalDocumentsError] = useState<
    string | undefined
  >();
  const [showHasAcceptedLegalDocumentsError, setShowHasAcceptedLegalDocumentsError] =
    useState<boolean>(false);

  // ----- Step 1 - Personal info -----
  const [buttonStep1Disabled, setButtonStep1Disabled] = useState<boolean>(true);
  const [profilePicture, setProfilePicture] = useState<
    | {
        base64: string;
        file: File;
      }
    | undefined
  >();
  const [fullName, setFullName] = useState<string>('');
  const [fullNameError, setFullNameError] = useState<string | undefined>();
  const [showFullNameError, setShowFullNameError] = useState<boolean>(false);
  const [displayName, setDisplayName] = useState<string>('');
  const [displayNameError, setDisplayNameError] = useState<string | undefined>();
  const [showDisplayNameError, setShowDisplayNameError] = useState<boolean>(false);
  const [countryCode, setCountryCode] = useState<{
    phone: string;
    code: string;
  }>({
    phone: '+351',
    code: 'PT',
  });
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [phoneNumberError, setPhoneNumberError] = useState<string | undefined>();
  const [showPhoneNumberError, setShowPhoneNumberError] = useState<boolean>(false);

  // ----- Step 2 - Optional info -----
  const [buttonStep2Disabled, setButtonStep2Disabled] = useState<boolean>(true);
  const [gender, setGender] = useState<UserGender | undefined>();
  const [language, setLanguage] = useState<BackendLanguageTag | undefined>();
  const [birthday, setBirthday] = useState<Date | undefined>();
  const [taxIdentificationNumber, setTaxIdentificationNumber] = useState<string>('');
  const [taxIdentificationNumberError, setTaxIdentificationNumberError] = useState<
    string | undefined
  >();
  const [showTaxIdentificationNumberError, setShowTaxIdentificationNumberError] =
    useState<boolean>(false);
  const [creatingAccount, setCreatingAccount] = useState<boolean>(false);
  const [createAccountError, setCreateAccountError] = useState<string | undefined>();

  // ----- Step 3 - Success -----
  const [isLoggingIn, setIsLoggingIn] = useState<boolean>(false);

  // Translations
  const {t} = useTranslation();

  // Get history and query parameters
  let {search} = useLocation();
  let history = useHistory();

  // Get invite
  useEffect(() => {
    // Validate the invite
    const getInvite = async (code: string) => {
      try {
        // Start loading
        setLoadingInviteData(true);

        // Send request
        const response = await InvitesRepository.instance.getInviteByVoucher<
          RelationshipInviteData & AffiliationInviteData
        >(code.trim());

        // Validate the invite
        if (
          (props.type === 'relationship' && response.type !== InviteType.Relationship) ||
          (props.type === 'affiliation' && response.type !== InviteType.Affiliation)
        ) {
          // Error
          throw new Error('Invalid invite type.');
        }

        // Set the invite data
        setInviteData({
          voucherCode: code.trim(),
          targetUserName:
            props.type === 'relationship' ? response.data?.targetUser?.name : undefined,
        });

        // Stop loading
        setLoadingInviteData(false);
      } catch (error) {
        // Error
        snackBarContext.setPublicToastNotification({
          message: t(translations.create_caregiver_account.errors.invalid_invite_error_message),
          severity: 'error',
        });

        // Get out
        history.replace(ScreenPaths.Login);
      }
    };

    // Look for the voucherCode
    if (props.type && search && search.split('?voucherCode=')[1]) {
      // Get the invite
      getInvite(search.split('?voucherCode=')[1]);
    } else {
      // Get out
      history.replace(ScreenPaths.Login);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Validation
  useEffect(() => {
    // Holds validation
    let emailValid: boolean = false;
    let passwordValid: boolean = false;
    let repeatPasswordValid: boolean = false;
    let legalDocumentsValid: boolean = false;
    let fullNameValid: boolean = false;
    let displayNameValid: boolean = false;
    let phoneNumberValid: boolean = false;
    let taxIdentificationNumberValid: boolean = false;

    // Email validation
    const validationEmail = emailValidation(email);
    emailValid = validationEmail.valid;
    if (validationEmail.error) {
      setEmailError(t([validationEmail.error]));
    } else {
      setEmailError(undefined);
    }

    // Password validation
    if (password.length > 0) {
      if (password.trim().length !== password.length) {
        if (showPasswordError) {
          // Error
          setPasswordError(
            t(
              translations.create_caregiver_account.errors
                .form_validation_invalid_password_blank_spaces
            )
          );
        }

        // Set it
        passwordValid = false;
      } else {
        if (PASSWORD_REGEX.test(password)) {
          if (passwordError !== undefined) {
            // No errors
            setPasswordError(undefined);
          }

          // Set it
          passwordValid = true;
        } else {
          if (showPasswordError) {
            // Error
            setPasswordError(
              t(
                translations.create_caregiver_account.errors
                  .form_validation_invalid_password_minimum_characters
              )
            );
          }

          // Set it
          passwordValid = false;
        }
      }
    } else {
      if (showPasswordError) {
        // Error
        setPasswordError(
          t(translations.create_caregiver_account.errors.form_validation_invalid_field)
        );
      }

      // Set it
      passwordValid = false;
    }

    // Repeat password validation
    if (repeatPassword.length > 0) {
      if (repeatPassword === password) {
        if (repeatPasswordError !== undefined) {
          // No errors
          setRepeatPasswordError(undefined);
        }

        // Set it
        repeatPasswordValid = true;
      } else {
        if (showRepeatPasswordError) {
          // Error
          setRepeatPasswordError(
            t(
              translations.create_caregiver_account.errors
                .form_validation_invalid_repeat_password_mismatch
            )
          );
        }

        // Set it
        repeatPasswordValid = false;
      }
    } else {
      if (showRepeatPasswordError) {
        // Error
        setRepeatPasswordError(
          t(translations.create_caregiver_account.errors.form_validation_invalid_field)
        );
      }

      // Set it
      repeatPasswordValid = false;
    }

    // Legal documents validation
    if (hasAcceptedLegalDocuments === true) {
      if (hasAcceptedLegalDocumentsError !== undefined) {
        // No errors
        setHasAcceptedLegalDocumentsError(undefined);
      }

      // Set it
      legalDocumentsValid = true;
    } else {
      if (showHasAcceptedLegalDocumentsError) {
        // Error
        setHasAcceptedLegalDocumentsError(
          t(translations.create_caregiver_account.errors.form_validation_invalid_legal_documents)
        );
      }

      // Set it
      legalDocumentsValid = false;
    }

    // Full name validation
    const fullNameValidation = nameValidation(FULL_NAME_MAX_CHARACTERS, fullName);
    fullNameValid = fullNameValidation.valid;
    if (fullNameValidation.valid) {
      setFullNameError(undefined);
    } else if (fullNameValidation.error) {
      setFullNameError(t([fullNameValidation.error], {max: fullNameValidation.max}));
    }

    // Display name validation
    const displayNameValidation = nameValidation(DISPLAY_NAME_MAX_CHARACTERS, displayName);
    displayNameValid = displayNameValidation.valid;
    if (displayNameValidation.valid) {
      setDisplayNameError(undefined);
    } else if (displayNameValidation.error) {
      setDisplayNameError(t([displayNameValidation.error], {max: displayNameValidation.max}));
    }

    // Phone number validation
    const phoneValidation = phoneNumberValidation(countryCode.phone, undefined, phoneNumber);
    phoneNumberValid = phoneValidation.valid;

    if (phoneValidation.valid) {
      setPhoneNumberError(undefined);
    } else if (!phoneValidation.valid && phoneValidation.error) {
      setPhoneNumberError(t([phoneValidation.error]));
    }

    // Tax identification number validation
    const taxPayerValidation = taxPayerNumberValidation(
      taxIdentificationNumber,
      authContext.state.session?.selectedAffiliation?.entity.region
    );
    taxIdentificationNumberValid = taxPayerValidation.valid;
    if (taxPayerValidation.error) {
      setTaxIdentificationNumberError(
        t([taxPayerValidation.error], {
          digits: taxPayerValidation.digits ? taxPayerValidation.digits : '',
        })
      );
    } else {
      setTaxIdentificationNumberError(undefined);
    }

    // Submit buttons validation
    if (activeStep === 0) {
      if (emailValid && passwordValid && repeatPasswordValid && legalDocumentsValid) {
        if (buttonStep0Disabled !== false) {
          // Valid
          setButtonStep0Disabled(false);
        }
      } else {
        if (buttonStep0Disabled !== true) {
          // Invalid
          setButtonStep0Disabled(true);
        }
      }
    } else if (activeStep === 1) {
      if (fullNameValid && displayNameValid && phoneNumberValid) {
        if (buttonStep1Disabled !== false) {
          // Valid
          setButtonStep1Disabled(false);
        }
      } else {
        if (buttonStep1Disabled !== true) {
          // Invalid
          setButtonStep1Disabled(true);
        }
      }
    } else if (activeStep === 2) {
      if (taxIdentificationNumberValid) {
        if (buttonStep2Disabled !== false) {
          // Valid
          setButtonStep2Disabled(false);
        }
      } else {
        if (buttonStep2Disabled !== true) {
          // Invalid
          setButtonStep2Disabled(true);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeStep,
    relationshipType,
    occupation,
    email,
    password,
    repeatPassword,
    hasAcceptedLegalDocuments,
    profilePicture,
    fullName,
    displayName,
    phoneNumber,
    countryCode,
    taxIdentificationNumber,
    birthday,
    gender,
    language,
    showEmailError,
    showOccupationError,
    showPasswordError,
    showRepeatPasswordError,
    showHasAcceptedLegalDocumentsError,
    showFullNameError,
    showDisplayNameError,
    showPhoneNumberError,
    showTaxIdentificationNumberError,
  ]);

  // Go to the next step
  const handleNextStep = () => {
    if (activeStep < 2) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  // Go back to the next step
  const handleStepBack = () => {
    if (activeStep > 0) {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
  };

  // Change field
  const handleChange = (
    value: string | undefined | null,
    field:
      | 'email'
      | 'password'
      | 'repeat-password'
      | 'full-name'
      | 'display-name'
      | 'phone-number'
      | 'tax-identification-number'
  ) => {
    let finalValue: string = '';
    if (value !== undefined && value !== null) {
      finalValue = value;
    }

    switch (field) {
      case 'email':
        setEmail(finalValue);
        break;
      case 'password':
        setPassword(finalValue);
        break;
      case 'repeat-password':
        setRepeatPassword(finalValue);
        break;
      case 'full-name':
        setFullName(finalValue);
        break;
      case 'display-name':
        setDisplayName(finalValue);
        break;
      case 'phone-number':
        setPhoneNumber(finalValue);
        break;
      case 'tax-identification-number':
        setTaxIdentificationNumber(finalValue);
        break;
      default:
        break;
    }
  };

  // Create the account
  const handleCreateAccount = async (event: any) => {
    // Prevent default
    event.preventDefault();

    // Loading
    setCreatingAccount(true);

    // Processed birthday
    // Get the day, month and year and set to UTC
    const processedBirthday: Date | undefined = birthday
      ? new Date(
          Date.UTC(
            new Date(birthday).getFullYear(),
            new Date(birthday).getMonth(),
            new Date(birthday).getDate(),
            0,
            0,
            0
          )
        )
      : undefined;

    try {
      // Request body
      const requestBody: PostCaregiverAccount = {
        product: appContext.state.configuration.product,
        type: UserType.Caregiver,
        name: fullName.trim(),
        isActive: true,
        password: password,
        inviteVoucherCode: inviteData?.voucherCode.trim() || '',
        inviteRelationshipType: props.type === 'relationship' ? relationshipType : undefined,
        occupationId: props.type === 'affiliation' ? occupation?.occupationId : undefined,
        displayName: displayName.trim() || undefined,
        email: email.trim() || undefined,
        phoneNumber: phoneNumber ? `${countryCode.phone}${phoneNumber.trim()}` : undefined,
        birthday: processedBirthday,
        taxPayerNumber: taxIdentificationNumber.trim() || undefined,
        gender: gender || undefined,
        languageCode: language || undefined,
      };

      // Create account
      const createdUser = await UsersRepository.instance.createAccount(requestBody);

      // Create the profile picture
      if (profilePicture) {
        // If the profile picture is not correctly updated we should not show an error.
        // The user must be able to login since their account was previously created.
        try {
          // Firebase auth login
          const userCredential: UserCredential = await signInWithEmailAndPassword(
            getAuth(),
            email.trim(),
            password
          );

          // Rehook backend auth
          await BackendApiManager.instance.hookUser(userCredential.user);

          // Request body
          const requestBodyProfilePictureFile: PostStorageRequest<UserPictureStorageRequestData> = {
            type: StorageType.UserPicture,
            fileType: StorageFileType.JPG,
            data: {
              userId: createdUser.id || '',
            },
          };

          // Some file was selected to be upload, request upload to the backend
          const storageProfilePictureRequestResult: StorageRequestResult<UserPictureStorageRequestData> =
            await StorageRequestRepository.instance.create<UserPictureStorageRequestData>(
              requestBodyProfilePictureFile
            );

          // Upload file
          const processedProfilePicture =
            await StorageRequestRepository.instance.upload<UserPictureStorageRequestData>(
              storageProfilePictureRequestResult,
              profilePicture.file
            );

          // Request body
          const updatedUserRequestBody: PutUser = {
            product: createdUser.product,
            isActive: createdUser.isActive,
            name: createdUser.name,
            type: createdUser.type,
            picture: processedProfilePicture,
          };

          // Update the user
          await UsersRepository.instance.updateById(createdUser.id || '', updatedUserRequestBody);
        } catch (error: any) {
          // Log error
          console.debug('Profile picture not created on legacy backend: ', error);
        }

        // Close backend
        await BackendApiManager.instance.hookUser(undefined);

        // Sign out from Firebase
        await getAuth()
          .signOut()
          .catch((error: any) => {
            // Log
            console.debug('Firebase sign out error in create account: ', error);
          });
      }

      // Success
      setActiveStep(3);
    } catch (error: any) {
      // Error for development
      if (appContext.state.configuration.environment === Environment.Development) {
        setCreateAccountError(
          error?.response?.data ? JSON.stringify(error.response.data as any) : ''
        );
      }

      // Error
      setActiveStep(4);
    }

    // Stop loading
    setCreatingAccount(false);
  };

  // Navigate to the login screen
  const handleNavigateToLogin = () => {
    history.replace(ScreenPaths.Login);
  };

  // Handle login
  const handleLogin = async () => {
    // Start loading
    setIsLoggingIn(true);

    try {
      // Login
      await authContext.signInAfterCreatingAccount({
        email: email.trim(),
        password: password,
      });
    } catch (error) {
      // Error
      snackBarContext.setPublicToastNotification({
        message: t(translations.create_caregiver_account.errors.enter_account_error_message),
        severity: 'error',
      });

      // Go to login
      history.replace(ScreenPaths.Login);
    }

    // Stop loading
    setIsLoggingIn(false);
  };

  const legalDocuments = getTranslatedLegalDocuments(
    appContext.state.configuration.legalDocuments,
    i18next.language
  );

  // Render the note
  const renderNote = () => (
    <Box className="create-caregiver-account-note-container">
      <FormLabel color="neutral" component="legend">
        {t(translations.create_caregiver_account.note_title)}
      </FormLabel>
      <Typography variant="body1" className="create-caregiver-account-note">
        {t(translations.create_caregiver_account.note_statement)}
      </Typography>
    </Box>
  );

  return (
    <>
      {loadingInviteData ? (
        <SplashScreen />
      ) : (
        <PublicContentPage>
          {activeStep <= 2 && (
            <>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                }}
                className="create-caregiver-account-title"
              >
                {t(translations.create_caregiver_account.register_title)}
              </Typography>
              <Box className="create-caregiver-account-stepper-container">
                <Stepper activeStep={activeStep} alternativeLabel>
                  {[
                    t(translations.create_caregiver_account.essential_information_step),
                    t(translations.create_caregiver_account.personal_information_step),
                    t(translations.create_caregiver_account.optional_information_step),
                  ].map((label) => {
                    return (
                      <Step key={`create-caregiver-account-step-${label}`}>
                        <StepLabel>{label}</StepLabel>
                      </Step>
                    );
                  })}
                </Stepper>
              </Box>
            </>
          )}
          {activeStep === 0 && (
            <>
              <Typography variant="body1" className="create-caregiver-account-step-description">
                {t(translations.create_caregiver_account.essential_information_step_description, {
                  brand: getBrandName(appContext.state.configuration.product),
                })}
              </Typography>
              <form>
                {props.type === 'relationship' && (
                  <Box className="create-caregiver-account-input">
                    <SelectInput
                      id="create-caregiver-account-relationship-type-select"
                      label={t(translations.create_caregiver_account.relationship_type_input, {
                        name: inviteData?.targetUserName,
                      })}
                      value={relationshipType}
                      handleChange={(value) => setRelationshipType(value as RelationshipType)}
                      items={relationshipTypes.map((obj) => {
                        return {value: obj.type, label: t(obj.translation)};
                      })}
                      required={true}
                    />
                  </Box>
                )}
                {props.type === 'affiliation' && (
                  <Box className="create-caregiver-account-input">
                    <OccupationInput
                      label={t(translations.create_caregiver_account.profession_input)}
                      value={occupation}
                      handleChange={(value?: OccupationOption) => setOccupation(value)}
                      handleBlur={() => {
                        if (!showOccupationError) setShowOccupationError(true);
                      }}
                      variant="outlined"
                      userType={UserType.Caregiver}
                    />
                  </Box>
                )}
                <Box className="create-caregiver-account-input">
                  <TextInput
                    label={t(translations.create_caregiver_account.email_address_input)}
                    value={email}
                    handleChange={(value) => handleChange(value, 'email')}
                    handleBlur={() => {
                      if (!showEmailError) setShowEmailError(true);
                    }}
                    error={emailError}
                    required={true}
                    type="email"
                  />
                </Box>
                <PasswordInput
                  className="create-caregiver-account-input"
                  label={t(translations.create_caregiver_account.password_input)}
                  value={password}
                  handleChange={(value) => handleChange(value, 'password')}
                  handleBlur={() => {
                    if (!showPasswordError) setShowPasswordError(true);
                  }}
                  error={passwordError}
                  required={true}
                />
                <PasswordInput
                  className="create-caregiver-account-input"
                  label={t(translations.create_caregiver_account.repeat_password_input)}
                  value={repeatPassword}
                  handleChange={(value) => handleChange(value, 'repeat-password')}
                  handleBlur={() => {
                    if (!showRepeatPasswordError) setShowRepeatPasswordError(true);
                  }}
                  error={repeatPasswordError}
                  required={true}
                />
                <Box className="create-caregiver-account-input">
                  <FormControl
                    error={hasAcceptedLegalDocumentsError !== undefined}
                    required
                    fullWidth
                    variant="standard"
                    component="fieldset"
                  >
                    <FormLabel color="neutral" component="legend">
                      {t(translations.create_caregiver_account.legal_documents_title)}
                    </FormLabel>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              if (event.target.name === LEGAL_DOCUMENTS_TARGET) {
                                setHasAcceptedLegalDocuments((prevValue) => !prevValue);
                              }

                              if (!showHasAcceptedLegalDocumentsError)
                                setShowHasAcceptedLegalDocumentsError(true);
                            }}
                            checked={hasAcceptedLegalDocuments}
                            name={LEGAL_DOCUMENTS_TARGET}
                          />
                        }
                        label={
                          <Trans
                            i18nKey={t(
                              translations.create_caregiver_account.legal_documents_statement
                            )}
                            components={[
                              <Link
                                target="_blank"
                                color="inherit"
                                href={legalDocuments.termsOfUseUrl}
                              />,
                              <Link
                                target="_blank"
                                color="inherit"
                                href={legalDocuments.privacyPolicyUrl}
                              />,
                            ]}
                          />
                        }
                      />
                    </FormGroup>
                    <FormHelperText>
                      {hasAcceptedLegalDocumentsError !== undefined
                        ? hasAcceptedLegalDocumentsError
                        : ''}
                    </FormHelperText>
                  </FormControl>
                </Box>
                {renderNote()}
                <Box className="create-caregiver-account-button-container">
                  <Button
                    onClick={handleNextStep}
                    disabled={buttonStep0Disabled}
                    endIcon={<ArrowForwardIcon />}
                    variant="contained"
                    size="medium"
                  >
                    {t(translations.create_caregiver_account.continue_button)}
                  </Button>
                </Box>
              </form>
            </>
          )}
          {activeStep === 1 && (
            <form>
              <EditPicture
                className="create-caregiver-account-profile-picture-container"
                type="user"
                label={t(translations.create_caregiver_account.profile_picture_input)}
                alt={t(translations.create_caregiver_account.profile_picture_input)}
                src={profilePicture?.base64}
                handleChange={(image?: {base64: string; file: File}) => {
                  if (image) {
                    setProfilePicture({
                      base64: image.base64,
                      file: image.file,
                    });
                  }
                }}
                useFileCropper={true}
                aspect={appSizes.profileImageSize.width / appSizes.profileImageSize.height}
                size={`${appSizes.profileImageSize.width}x${appSizes.profileImageSize.height}`}
                clearable={false}
                alertMessage={t(translations.create_caregiver_account.profile_picture_info_message)}
              />
              <Box className="create-caregiver-account-input">
                <TextInput
                  label={t(translations.create_caregiver_account.full_name_input)}
                  value={fullName}
                  handleChange={(value) => handleChange(value, 'full-name')}
                  handleBlur={() => {
                    if (!showFullNameError) setShowFullNameError(true);
                  }}
                  characterCountError={
                    fullNameError !== undefined
                      ? {
                          error: fullNameError,
                          characterCount: fullName.trim().length,
                          maxCharacters: FULL_NAME_MAX_CHARACTERS,
                        }
                      : undefined
                  }
                  required={true}
                  type="text"
                />
              </Box>
              <Box className="create-caregiver-account-input">
                <TextInput
                  label={t(translations.create_caregiver_account.display_name_input)}
                  value={displayName}
                  handleChange={(value) => handleChange(value, 'display-name')}
                  handleBlur={() => {
                    if (!showDisplayNameError) setShowDisplayNameError(true);
                  }}
                  characterCountError={
                    displayNameError !== undefined
                      ? {
                          error: displayNameError,
                          characterCount: displayName.trim().length,
                          maxCharacters: DISPLAY_NAME_MAX_CHARACTERS,
                        }
                      : undefined
                  }
                  required={true}
                  type="text"
                />
              </Box>
              <Alert severity="info" className="create-caregiver-account-alert">
                {t(translations.create_caregiver_account.display_name_info_message)}
              </Alert>
              <Box className="create-caregiver-account-input">
                <PhoneNumberInput
                  label={t(translations.create_caregiver_account.mobile_number_input)}
                  countryCode={countryCode}
                  phoneNumber={phoneNumber}
                  handleChangeCountryCode={(value: {code: string; phone: string}) =>
                    setCountryCode(value)
                  }
                  handleChangePhoneNumber={(value) => handleChange(value, 'phone-number')}
                  handleBlur={() => {
                    if (!showPhoneNumberError) setShowPhoneNumberError(true);
                  }}
                  error={phoneNumberError}
                  countryCodeWidth="60%"
                  phoneNumberWidth="40%"
                  marginForSmallDevices="22px"
                />
              </Box>
              {renderNote()}
              <Box className="create-caregiver-account-button-container">
                <Button
                  onClick={handleNextStep}
                  disabled={buttonStep1Disabled}
                  endIcon={<ArrowForwardIcon />}
                  variant="contained"
                  size="medium"
                >
                  {t(translations.create_caregiver_account.continue_button)}
                </Button>
              </Box>
              <Box className="create-caregiver-account-button-container">
                <Button
                  onClick={handleStepBack}
                  startIcon={<ArrowBackIcon />}
                  variant="text"
                  size="medium"
                >
                  {t(translations.create_caregiver_account.go_back_button)}
                </Button>
              </Box>
            </form>
          )}
          {activeStep === 2 && (
            <form onSubmit={handleCreateAccount}>
              <Box className="create-caregiver-account-input">
                <FlatSelectDate
                  label={t(translations.create_caregiver_account.birthday_input)}
                  placeholder={t(translations.create_caregiver_account.birthday_input_placeholder)}
                  value={birthday}
                  onChange={(date) => setBirthday(date || undefined)}
                  disabled={creatingAccount}
                  disableFuture={true}
                />
              </Box>
              <Box className="create-caregiver-account-input">
                <SelectInput
                  id="create-caregiver-account-gender-select"
                  label={t(translations.create_caregiver_account.gender_input)}
                  placeholder={t(translations.create_caregiver_account.gender_placeholder_input)}
                  value={gender}
                  clearable={true}
                  handleChange={(value) =>
                    setGender(
                      (value && value.trim().length > 0 ? value : undefined) as
                        | UserGender
                        | undefined
                    )
                  }
                  disabled={creatingAccount}
                  items={genders.map((obj) => {
                    return {value: obj.type, label: t(obj.translation)};
                  })}
                />
              </Box>
              <Box className="create-caregiver-account-input">
                <SelectInput
                  id="create-caregiver-account-language-select"
                  label={t(translations.create_caregiver_account.language_input)}
                  placeholder={t(translations.create_caregiver_account.language_placeholder_input)}
                  value={language}
                  clearable={true}
                  handleChange={(value) =>
                    setLanguage(
                      (value && value.trim().length > 0 ? value : undefined) as
                        | BackendLanguageTag
                        | undefined
                    )
                  }
                  disabled={creatingAccount}
                  items={languages.map((obj) => {
                    return {value: obj.type, label: t(obj.translation)};
                  })}
                />
              </Box>
              <Box className="create-caregiver-account-input">
                <TextInput
                  label={t(translations.create_caregiver_account.tax_identification_number_input)}
                  value={taxIdentificationNumber}
                  handleChange={(value) => handleChange(value, 'tax-identification-number')}
                  handleBlur={() => {
                    if (!showTaxIdentificationNumberError)
                      setShowTaxIdentificationNumberError(true);
                  }}
                  error={taxIdentificationNumberError}
                  type="text"
                  disabled={creatingAccount}
                />
              </Box>
              <Box className="create-caregiver-account-button-container">
                <LoadingButton
                  loading={creatingAccount}
                  loadingPosition="end"
                  endIcon={<PersonAddAlt1Icon />}
                  disabled={buttonStep2Disabled}
                  fullWidth
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="medium"
                >
                  {t(translations.create_caregiver_account.create_account_button)}
                </LoadingButton>
              </Box>
              <Box className="create-caregiver-account-button-container">
                <Button
                  onClick={handleStepBack}
                  disabled={creatingAccount}
                  startIcon={<ArrowBackIcon />}
                  variant="text"
                  size="medium"
                >
                  {t(translations.create_caregiver_account.go_back_button)}
                </Button>
              </Box>
            </form>
          )}
          {activeStep === 3 && (
            <>
              <Box className="create-caregiver-account-result-icon-container">
                <CheckCircleIcon
                  sx={{
                    color: 'success.main',
                    fontSize: '70px',
                  }}
                />
              </Box>
              <Typography
                variant="h6"
                sx={{fontWeight: 'bold'}}
                className="create-caregiver-account-result-title"
              >
                {t(translations.create_caregiver_account.success_title)}
              </Typography>
              <Typography variant="body1" className="create-caregiver-account-result-description">
                {t(translations.create_caregiver_account.success_description, {
                  brand: getBrandName(appContext.state.configuration.product),
                })}
              </Typography>
              <Box className="create-caregiver-account-button-container">
                <LoadingButton
                  onClick={handleLogin}
                  loading={isLoggingIn}
                  loadingPosition="end"
                  endIcon={<LoginIcon />}
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="medium"
                >
                  {t(translations.create_caregiver_account.start_using_button)}
                </LoadingButton>
              </Box>
            </>
          )}
          {activeStep === 4 && (
            <>
              <Box className="create-caregiver-account-result-icon-container">
                <CancelIcon
                  sx={{
                    color: 'error.main',
                    fontSize: '70px',
                  }}
                />
              </Box>
              <Typography
                variant="h6"
                sx={{fontWeight: 'bold'}}
                className="create-caregiver-account-result-title"
              >
                {t(translations.create_caregiver_account.error_title)}
              </Typography>
              <Typography variant="body1" className="create-caregiver-account-result-description">
                {t(translations.create_caregiver_account.error_description)}
              </Typography>
              {appContext.state.configuration.environment === Environment.Development &&
                createAccountError && (
                  <Box
                    className="create-caregiver-account-dev-error-container"
                    sx={{
                      borderColor: 'error.main',
                    }}
                  >
                    <Typography variant="body1" className="create-caregiver-account-dev-error">
                      {createAccountError}
                    </Typography>
                  </Box>
                )}
              <Box className="create-caregiver-account-button-container">
                <Button
                  onClick={handleNavigateToLogin}
                  endIcon={<ArrowForwardIcon />}
                  variant="contained"
                  size="medium"
                >
                  {t(translations.create_caregiver_account.go_back_to_login_button)}
                </Button>
              </Box>
            </>
          )}
        </PublicContentPage>
      )}
    </>
  );
}
